'use client';

import { PointerEvent, ReactNode } from 'react';

import { cn } from '@/lib/utils';

import AnimatedEl from '@/lib/motion';
import {
  useMarqueeAnimation,
  UseMarqueeAnimationParams,
} from './useMarqueeAnimation';

export type MarqueeProps = {
  config?: UseMarqueeAnimationParams;
  /**
   * Amount of space between repeated items
   * @default 0
   */
  gap?: number;
  /**
   * Number of times to repeat items
   */
  repeat: number;
  children: ReactNode;
  /**
   * WARNING: refrain from using classes that affect spacing between children
   * e.g gap, margin, padding, etc. They break the illusion of a seamless
   * repeating marquee. Use the `gap` prop instead.
   * @example
   * <Marquee classNames={{ root: 'gap-4' }} /> // bad!
   * <Marquee gap={16} /> // this is good
   */
  classNames?: {
    root?: string;
    inner?: string;
  };
};

export function Marquee({
  children,
  classNames,
  gap = 0,
  repeat,
  config = {},
}: MarqueeProps) {
  const { x, playMarquee, pauseMarquee, prepareDrag, endDrag, updateDrag } =
    useMarqueeAnimation(config);

  const onHoverStart = () => {
    pauseMarquee();
  };
  const onHoverEnd = () => {
    playMarquee();
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    prepareDrag(event.clientX);
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.style.cursor = 'grabbing';

    updateDrag(event.clientX);
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    endDrag(event.clientX);
  };

  return (
    <AnimatedEl
      el='section'
      className={cn('flex w-full overflow-hidden', classNames?.root)}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      onPointerMove={onPointerMove}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
    >
      {Array.from({ length: repeat }, (_, i) => i).map((i) => (
        <AnimatedEl
          forceAnimation={{
            desktop: 'force-enable',
            mobile: 'force-enable',
          }}
          el='div'
          key={i}
          className={cn('flex shrink-0', classNames?.inner)}
          aria-hidden={i > 0}
          // The gap between the repeated items is added to each item to
          // keep the illusion of a seamless repeating marquee.
          // TODO: implement this more elegantly and perhaps support responsiveness
          style={{ x, paddingRight: gap }}
        >
          {children}
        </AnimatedEl>
      ))}
    </AnimatedEl>
  );
}
